import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ReceiptText, Loader2, Truck, RotateCcw, Ban, AlertTriangle, PackageX } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatCard } from "@/components/kit/StatCard";
import { ExportButton } from "@/components/kit/ExportButton";
import { EmptyState } from "@/components/feedback/EmptyState";
import { invoicingService, storesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";

export default function InvoicingPage() {
  const [store, setStore] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const storesQuery = useQuery({ queryKey: ["stores", "lite"], queryFn: () => storesService.list() });
  const stores = useMemo(() => storesQuery.data?.items || [], [storesQuery.data]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await invoicingService.generate({
        store_id: store === "all" ? null : store,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      setSummary(res.summary);
      toast.success("Summary generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const metrics = summary
    ? [
        { key: "orders_shipped", label: "Orders Shipped", value: summary.orders_shipped, icon: Truck, tone: "green" },
        { key: "orders_returned", label: "Orders Returned", value: summary.orders_returned, icon: RotateCcw, tone: "amber" },
        { key: "orders_cancelled", label: "Orders Cancelled", value: summary.orders_cancelled, icon: Ban, tone: "neutral" },
        { key: "quantity_problems", label: "Quantity Problems", value: summary.quantity_problems, icon: AlertTriangle, tone: "red" },
        { key: "didnt_arrive", label: "Didn't Arrive", value: summary.didnt_arrive, icon: PackageX, tone: "red" },
      ]
    : [];

  const exportColumns = [
    { label: "Store", get: () => summary?.store_name },
    { label: "Date From", get: () => summary?.date_from || "All time" },
    { label: "Date To", get: () => summary?.date_to || "All time" },
    { label: "Orders Shipped", key: "orders_shipped" },
    { label: "Orders Returned", key: "orders_returned" },
    { label: "Orders Cancelled", key: "orders_cancelled" },
    { label: "Quantity Problems", key: "quantity_problems" },
    { label: "Didn't Arrive", key: "didnt_arrive" },
  ];

  return (
    <div className="space-y-6" data-testid="page-invoicing">
      <PageHeader
        sticky title="Invoicing" description="Generate an operational billing summary for a store and date range."
        actions={summary ? <ExportButton filename="invoice-summary" columns={exportColumns} rows={[summary]} testId="invoicing-export" /> : null}
      />

      <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Store</label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger className="h-10 bg-background" data-testid="invoicing-store"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((st) => (<SelectItem key={st.id} value={st.id}>{st.store_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Date From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="invoicing-date-from" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Date To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm shadow-soft focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" data-testid="invoicing-date-to" />
          </div>
          <div className="flex items-end">
            <Button className="h-10 w-full gap-2" onClick={generate} disabled={loading} data-testid="invoicing-generate">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
              Generate Summary
            </Button>
          </div>
        </div>
      </div>

      {summary ? (
        <div>
          <p className="mb-4 text-sm text-muted-foreground" data-testid="invoicing-summary-caption">
            <span className="font-medium text-foreground">{summary.store_name}</span> · {summary.date_from || "All time"} → {summary.date_to || "now"}
          </p>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            {metrics.map((m) => (
              <StatCard key={m.key} testId={`invoicing-${m.key}`} label={m.label} value={m.value} icon={m.icon} tone={m.tone} />
            ))}
          </div>
        </div>
      ) : (
        <EmptyState icon={ReceiptText} title="No summary yet" testId="invoicing-empty"
          description="Choose a store and date range, then generate a summary." />
      )}
    </div>
  );
}
